<template>
    <section v-if="products" v-editable="blok" class="parking-options-section airport-page-section">
        <div class="parking-options-section__container md:container">
            <h2 class="parking-options-section__title airport-page-title">
                {{ blok.title }}
            </h2>
            <RichText :richtext="blok.description" class="parking-options-section__descr" />
            <div class="parking-options-section__map">
                <button class="map-btn" :class="{ 'is-open': mapOpen }" @click="mapOpen = !mapOpen">
                    <svg
                        class="map-btn__icon-open"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="25"
                        viewBox="0 0 24 25"
                        fill="none"
                    >
                        <path
                            d="M20.5 3.5L20.34 3.53L15 5.6L9 3.5L3.36 5.4C3.15 5.47 3 5.65 3 5.88V21C3 21.1326 3.05268 21.2598 3.14645 21.3536C3.24021 21.4473 3.36739 21.5 3.5 21.5L3.66 21.47L9 19.4L15 21.5L20.64 19.6C20.85 19.53 21 19.35 21 19.12V4C21 3.86739 20.9473 3.74021 20.8536 3.64645C20.7598 3.55268 20.6326 3.5 20.5 3.5ZM10 5.97L14 7.37V19.03L10 17.63V5.97ZM5 6.96L8 5.95V17.65L5 18.81V6.96ZM19 18.04L16 19.05V7.36L19 6.2V18.04Z"
                            fill="currentColor"
                        />
                    </svg>
                    <svg
                        class="map-btn__icon-close"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="25"
                        viewBox="0 0 24 25"
                        fill="none"
                    >
                        <path
                            d="M12 20.5C7.59 20.5 4 16.91 4 12.5C4 8.09 7.59 4.5 12 4.5C16.41 4.5 20 8.09 20 12.5C20 16.91 16.41 20.5 12 20.5ZM12 2.5C6.47 2.5 2 6.97 2 12.5C2 18.03 6.47 22.5 12 22.5C17.53 22.5 22 18.03 22 12.5C22 6.97 17.53 2.5 12 2.5ZM14.59 8.5L12 11.09L9.41 8.5L8 9.91L10.59 12.5L8 15.09L9.41 16.5L12 13.91L14.59 16.5L16 15.09L13.41 12.5L16 9.91L14.59 8.5Z"
                            fill="currentColor"
                        />
                    </svg>
                    {{ mapBtnText }}
                </button>
                <ClientOnly>
                    <LazyProductMap v-if="mapOpen" :airport="blok.airport" :products="parkingOptions" class="pt-5" />
                </ClientOnly>
            </div>
            <ul class="parking-options-section__products">
                <li v-for="product in products" :key="product.id" class="parking-options-section__item">
                    <NuxtLink :to="domain + localePath(getURL(product))" class="p-card">
                        <NuxtImg
                            v-if="product.id"
                            :src="`https://cdn2.looking4.com/media/images/products/${product.id}.jpg`"
                            :title="`${product.displayName} logo`"
                            :alt="`${product.displayName} logo`"
                            format="webp"
                            loading="lazy"
                        />
                        <h3 class="p-card__title">{{ product.displayName }}</h3>
                    </NuxtLink>
                </li>
            </ul>
            <div v-if="showShowMoreBtn" class="parking-options-section__btn">
                <ShowMoreBtn
                    :is-opened="productsOpen"
                    :show-text="t('show_all_parking_providers')"
                    :hide-text="t('hide_parking_providers')"
                    @click="productsOpen = !productsOpen"
                />
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { useWindowSize } from '@vueuse/core';
import type { IL4CarParkingOptions } from '~/types/components/L4CarParkingOptions';
import type { IL4ApiProduct } from '~/types/components/L4Products';
const { t } = useI18n();

const { blok } = defineProps<{ blok: IL4CarParkingOptions }>();
const route: any = useRoute();
const localePath = useLocalePath();
const domain = useRuntimeConfig().public.SITE_URL;

const mapOpen = ref(false);
const productsOpen = ref(false);

function getURL(product: IL4ApiProduct) {
    const urlString = `/${route.params.type}/${route.params.location}/${product.titleSlug}/${product.id}`;
    return localePath(urlString);
}

const mapBtnText = computed(() => {
    return mapOpen.value ? t('close_map') : t('view_parking_options_map');
});

watch(
    () => blok,
    () => {
        if (blok?.open_state) productsOpen.value = true;
    },
    { immediate: true }
);

const { width } = useWindowSize();
const parkingOptions = inject<Ref<IL4ApiProduct[]>>('apiParkingProducts');

const showShowMoreBtn = computed<boolean>(() => {
    // Max number of products to display in collapsed state
    const defaultDisplayedProductsNumber = width.value < 768 ? 4 : 5;
    return parkingOptions.value.length > defaultDisplayedProductsNumber;
});

const products = computed<IL4ApiProduct[]>(() => {
    // Max number of products to display in collapsed state
    const defaultDisplayedProductsNumber = width.value < 768 ? 4 : 5;
    return productsOpen.value ? parkingOptions.value : parkingOptions.value?.slice(0, defaultDisplayedProductsNumber);
});
</script>

<style lang="postcss" scoped>
.parking-options-section {
    &__title {
        @apply mb-3;
    }
    &__descr {
        @apply mb-6 md:mb-8 text-xl;
    }
    &__map {
        @apply mb-6 md:mb-8;
    }
    &__products {
        @apply grid grid-cols-2 md:grid-cols-5 gap-x-3 gap-y-6 md:gap-x-5 mb-6;
    }
    &__btn {
        @apply text-center md:text-right;
    }
    &__item {
        & > .p-card {
            height: 100%;
        }
    }
}

.p-card {
    @apply p-3 bg-white rounded block;
    border: 1px solid #ebebef;
    box-shadow: 0px 2px 2px 0px rgba(209, 209, 209, 0.25);
    &:hover {
        box-shadow: 0px 2px 4px 2px rgba(209, 209, 209, 0.5);
    }
    &__image {
        @apply w-full mb-2;
    }
    &__title {
        @apply font-semibold text-[#180B1E] text-base leading-5;
    }
}

.map-btn {
    @apply inline-flex items-center text-[#0094FF] font-semibold transition-colors;
    @media (hover: hover) and (pointer: fine) {
        &:hover {
            @apply text-[#0084E3];
        }
    }
    &.is-open {
        @apply text-[#D8174C];
        @media (hover: hover) and (pointer: fine) {
            &:hover {
                @apply text-[#840628];
            }
        }
        .map-btn__icon-open {
            @apply hidden;
        }
        .map-btn__icon-close {
            @apply block;
        }
    }
    .map-btn__icon-close {
        @apply hidden;
    }
    .map-btn__icon-open,
    .map-btn__icon-close {
        @apply mr-2 shrink-0 h-6 w-6;
    }
}

:deep(.rich-text p) {
    @apply text-xl leading-[30px] mb-8 mt-0;
}
</style>
